/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\DeviceModelFilePreview.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Device Models module.
----------------------------------------------------------------*/

import React from 'react';
import { createPortal } from 'react-dom';
import { FileCode, Download, X } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useI18n } from '../../../hooks/useI18n';

interface DeviceModelFilePreviewProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  content: string;
  onDownload: () => void;
}

export function DeviceModelFilePreview({ isOpen, onClose, fileName, content, onDownload }: DeviceModelFilePreviewProps) {
  const { t } = useI18n();

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 h-[85vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-4 border-b border-border gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-primary flex-shrink-0"><FileCode className="h-5 w-5" /></div>
            <h3 className="text-lg font-semibold text-foreground truncate">{fileName}</h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-xs text-muted-foreground mr-2">{t('common.editorReadOnlyHint', 'Read Only - Press Ctrl+F to search')}</div>
            <button onClick={onDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-md text-sm font-medium transition-colors border border-primary/20"
              title="Download file">
              <Download className="h-4 w-4" /><span>Download</span>
            </button>
            <div className="w-px h-6 bg-border mx-1" />
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors" aria-label="Close">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden bg-[#1e1e1e] dark:bg-[#1e1e1e] border-b border-border">
          <Editor height="100%"
            defaultLanguage={fileName.endsWith('.xml') ? 'xml' : 'ini'}
            theme={document.documentElement.classList.contains('dark') ? 'vs-dark' : 'light'}
            value={content || ''}
            options={{
              readOnly: true, minimap: { enabled: true }, fontSize: 13, scrollBeyondLastLine: false,
              wordWrap: 'on', automaticLayout: true, padding: { top: 16, bottom: 16 },
              fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, 'Courier New', monospace",
              fontLigatures: true, renderWhitespace: 'none', domReadOnly: true
            }}
            loading={<div className="flex items-center justify-center h-full text-muted-foreground">Loading editor...</div>}
          />
        </div>
        <div className="flex items-center justify-end p-4 border-t border-border flex-shrink-0 bg-background/50 dark:bg-gray-800/50 rounded-b-lg">
          <button onClick={onClose}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2">
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
